import type { ChainModifiers, Entry, EntryFieldTypes, EntrySkeletonType, LocaleCode } from "contentful";
import type { TypeButtonSkeleton } from "./TypeButton";
import type { TypeContent_liteSkeleton } from "./TypeContent_lite";

/**
 * Fields type definition for content type 'TypeBanner'
 * @name TypeBannerFields
 * @type {TypeBannerFields}
 * @memberof TypeBanner
 */
export interface TypeBannerFields {
    /**
     * Field type definition for field 'name' (Name)
     * @name Name
     * @localized false
     */
    name: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'section_id' (Section ID)
     * @name Section ID
     * @localized false
     * @summary Section ID used for navigation. Note: Only applicable when this component is being used as a section.
     */
    section_id?: EntryFieldTypes.Symbol;
    /**
     * Field type definition for field 'variation' (Variation)
     * @name Variation
     * @localized false
     */
    variation: EntryFieldTypes.Symbol<"cta" | "lto">;
    /**
     * Field type definition for field 'mobile_orientation' (Mobile Orientation)
     * @name Mobile Orientation
     * @localized false
     * @summary To enable Horizontal orientation in the LTO Variation, ensure the button label is less than 12 characters.
     */
    mobile_orientation?: EntryFieldTypes.Symbol<"Horizontal" | "Vertical">;
    /**
     * Field type definition for field 'sticky' (Sticky)
     * @name Sticky
     * @localized false
     */
    sticky?: EntryFieldTypes.Boolean;
    /**
     * Field type definition for field 'sticky_type' (Sticky Type)
     * @name Sticky Type
     * @localized false
     * @summary Enabling the Half Banner option in the LTO Variation hides the main content on mobile devices once the page is scrolled.
     */
    sticky_type?: EntryFieldTypes.Symbol<"Full Banner" | "Half Banner">;
    /**
     * Field type definition for field 'main_content' (Main Content)
     * @name Main Content
     * @localized false
     */
    main_content?: EntryFieldTypes.EntryLink<TypeContent_liteSkeleton>;
    /**
     * Field type definition for field 'button' (Button)
     * @name Button
     * @localized false
     */
    button?: EntryFieldTypes.EntryLink<TypeButtonSkeleton>;
    /**
     * Field type definition for field 'show_date' (Show Date)
     * @name Show Date
     * @localized false
     */
    show_date?: EntryFieldTypes.Date;
    /**
     * Field type definition for field 'start_date' (Start Date)
     * @name Start Date
     * @localized false
     */
    start_date?: EntryFieldTypes.Date;
    /**
     * Field type definition for field 'end_date' (End Date)
     * @name End Date
     * @localized false
     * @summary When set, a countdown clock will automatically display on the banner
     */
    end_date?: EntryFieldTypes.Date;
    /**
     * Field type definition for field 'removal_date' (Removal Date)
     * @name Removal Date
     * @localized false
     */
    removal_date?: EntryFieldTypes.Date;
    /**
     * Field type definition for field 'background_color' (Background Color)
     * @name Background Color
     * @localized false
     */
    background_color?: EntryFieldTypes.Symbol<"--surface-neutral-black" | "--surface-neutral-dark" | "--surface-neutral-light" | "--surface-neutral-mid" | "--surface-neutral-white" | "--surface-primary-1" | "--surface-primary-2" | "--surface-primary-3" | "--surface-primary-4" | "--surface-primary-5" | "--surface-primary-6">;
    /**
     * Field type definition for field 'countdown_background_color' (Countdown Background Color)
     * @name Countdown Background Color
     * @localized false
     */
    countdown_background_color?: EntryFieldTypes.Symbol<"--surface-neutral-black" | "--surface-neutral-dark" | "--surface-neutral-light" | "--surface-neutral-mid" | "--surface-neutral-white" | "--surface-primary-1" | "--surface-primary-2" | "--surface-primary-3" | "--surface-primary-4" | "--surface-primary-5" | "--surface-primary-6">;
    /**
     * Field type definition for field 'countdown_text_color' (Countdown Text Color)
     * @name Countdown Text Color
     * @localized false
     */
    countdown_text_color?: EntryFieldTypes.Symbol<"--text-neutral-dark" | "--text-neutral-inverse-dark" | "--text-neutral-inverse-light" | "--text-neutral-light" | "--text-neutral-mid" | "--text-primary-1" | "--text-primary-2" | "--text-primary-3" | "--text-primary-4" | "--text-primary-5" | "--text-primary-6">;
    /**
     * Field type definition for field 'skip_clone' (Skip Clone)
     * @name Skip Clone
     * @localized false
     * @summary Disable cloning of this entry via the Clone Two You Plugin
     */
    skip_clone?: EntryFieldTypes.Boolean;
}

/**
 * Entry skeleton type definition for content type 'banner' (Banner)
 * @name TypeBannerSkeleton
 * @type {TypeBannerSkeleton}
 * @author 4m1b9L6VfuUe5HiWpYWSiQ
 * @since 2024-07-10T16:11:38.453Z
 * @version 35
 */
export type TypeBannerSkeleton = EntrySkeletonType<TypeBannerFields, "banner">;
/**
 * Entry type definition for content type 'banner' (Banner)
 * @name TypeBanner
 * @type {TypeBanner}
 * @author 4m1b9L6VfuUe5HiWpYWSiQ
 * @since 2024-07-10T16:11:38.453Z
 * @version 35
 */
export type TypeBanner<Modifiers extends ChainModifiers, Locales extends LocaleCode = LocaleCode> = Entry<TypeBannerSkeleton, Modifiers, Locales>;

export function isTypeBanner<Modifiers extends ChainModifiers, Locales extends LocaleCode>(entry: Entry<EntrySkeletonType, Modifiers, Locales>): entry is TypeBanner<Modifiers, Locales> {
    return entry.sys.contentType.sys.id === 'banner'
}

export type TypeBannerWithoutLinkResolutionResponse = TypeBanner<"WITHOUT_LINK_RESOLUTION">;
export type TypeBannerWithoutUnresolvableLinksResponse = TypeBanner<"WITHOUT_UNRESOLVABLE_LINKS">;
export type TypeBannerWithAllLocalesResponse<Locales extends LocaleCode = LocaleCode> = TypeBanner<"WITH_ALL_LOCALES", Locales>;
export type TypeBannerWithAllLocalesAndWithoutLinkResolutionResponse<Locales extends LocaleCode = LocaleCode> = TypeBanner<"WITHOUT_LINK_RESOLUTION" | "WITH_ALL_LOCALES", Locales>;
export type TypeBannerWithAllLocalesAndWithoutUnresolvableLinksResponse<Locales extends LocaleCode = LocaleCode> = TypeBanner<"WITHOUT_UNRESOLVABLE_LINKS" | "WITH_ALL_LOCALES", Locales>;
